import { useState, useCallback } from 'react';
import JSZip from 'jszip';
import { useAuth } from '@frontend/context/authContext';
import { backendServerApiEndpoint } from '@frontend/applications/Shared/serviceHosts';

interface DownloadResult {
  successful: string[];
  failed: string[];
}

interface UseImageDownloaderProps {
  onProgress?: (progress: number) => void;
}

interface MediaItem {
  content_type: string;
  media_type: string;
  url: string;
}

interface ContentData {
  data: Array<{
    id: string;
    media: MediaItem[];
    member?: {
      name: string;
    };
    social_post?: {
      id: string;
    };
  }>;
}

export interface DownloadItem {
  entityId: string;
  postPermalink: string;
}

const BATCH_SIZE = 20;
const CONTENT_API_PATH = 'licensed_content/list';

export const useImageDownloader = ({ onProgress }: UseImageDownloaderProps = {}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const { clientInfo, token } = useAuth();
  const clientId = clientInfo?.id;

  const downloadImages = useCallback(
    async (items: DownloadItem[], baseZipFilename: string): Promise<DownloadResult> => {
      setIsDownloading(true);
      setProgress(0);
      onProgress?.(0);

      const result: DownloadResult = {
        successful: [],
        failed: [],
      };

      try {
        // Fetch content data for all entity IDs
        const contentResponse = await fetch(`${backendServerApiEndpoint()}/${CONTENT_API_PATH}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            client_id: clientId,
            post_ids: items.map((item) => item.entityId).join(','),
          }),
        });

        if (!contentResponse.ok) {
          throw new Error(`Failed to fetch content data: ${contentResponse.status}`);
        }

        const contentData: ContentData = await contentResponse.json();

        if (!contentData?.data?.length) {
          throw new Error('No content data found for the provided entity IDs');
        }

        // Get the first 5 records
        const contentRecords = contentData.data;

        // Process content in batches
        const totalBatches = Math.ceil(contentRecords.length / BATCH_SIZE);

        // Create a progress tracker object
        const progressTracker = {
          total: totalBatches,
          processed: 0,
          updateProgress: () => {
            progressTracker.processed++;
            const progress = progressTracker.processed / progressTracker.total;
            setProgress(progress);
            onProgress?.(progress);
          },
        };

        for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
          const startIndex = batchIndex * BATCH_SIZE;
          const endIndex = Math.min(startIndex + BATCH_SIZE, contentRecords.length);
          const batchContent = contentRecords.slice(startIndex, endIndex);

          const zip = new JSZip();

          // Process each content item in the batch
          const downloadPromises = batchContent.map(async (content) => {
            const item = items.find((dataItem) => {
              return content.social_post && dataItem.entityId === String(content.social_post.id);
            });

            const mediaPromises = content.media.map(async (media, index) => {
              // Determine extension first
              const contentType = media.content_type;
              let extension = 'jpg'; // default extension

              // Handle different media types based on content_type
              if (contentType === 'video') {
                extension = 'mp4';
              } else if (contentType === 'image/png' || contentType === 'png') {
                extension = 'png';
              } else if (contentType === 'image/jpeg' || contentType === 'jpeg' || contentType === 'jpg') {
                extension = 'jpg';
              } else if (contentType === 'image/gif' || contentType === 'gif') {
                extension = 'gif';
              } else if (contentType === 'image/webp' || contentType === 'webp') {
                extension = 'webp';
              } else if (contentType === 'video/mp4' || contentType === 'mp4') {
                extension = 'mp4';
              } else if (contentType === 'video/quicktime' || contentType === 'mov') {
                extension = 'mov';
              } else if (contentType === 'video/webm' || contentType === 'webm') {
                extension = 'webm';
              } else if (contentType === 'video/ogg' || contentType === 'ogg') {
                extension = 'ogg';
              }

              const baseFilename = item?.postPermalink
                ? item.postPermalink.replace(/^https?:\/\//, '').replace(/[^a-zA-Z0-9-]/g, '_')
                : content.id;
              const filename = `${baseFilename}_${index + 1}.${extension}`;

              try {
                const response = await fetch(media.url);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const blob = await response.blob();
                if (!blob || blob.size === 0) throw new Error('Empty or invalid blob received');

                zip.file(filename, blob);
                return { url: media.url, success: true };
              } catch (error) {
                // Try proxy as fallback
                try {
                  const proxyResponse = await fetch(
                    `/api/object_storage/download?url=${encodeURIComponent(media.url)}`,
                    {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    },
                  );

                  if (!proxyResponse.ok) throw new Error(`Proxy failed: ${proxyResponse.status}`);

                  const blob = await proxyResponse.blob();
                  if (!blob || blob.size === 0) throw new Error('Empty or invalid blob received from proxy');

                  zip.file(filename, blob);
                  return { url: media.url, success: true };
                } catch (proxyError) {
                  return { url: media.url, success: false };
                }
              }
            });

            const mediaResults = await Promise.all(mediaPromises);

            return {
              entityId: content.id,
              mediaResults,
            };
          });

          // Wait for all content items in this batch to complete
          const batchResults = await Promise.all(downloadPromises);

          // Process batch results
          batchResults.forEach(({ mediaResults }) => {
            mediaResults.forEach(({ url, success }) => {
              if (success) {
                result.successful.push(url);
              } else {
                result.failed.push(url);
              }
            });
          });

          // Generate and download zip file for this batch
          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const downloadUrl = window.URL.createObjectURL(zipBlob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = `${baseZipFilename}_batch_${batchIndex + 1}.zip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(downloadUrl);

          // Update progress after each batch is completed
          progressTracker.updateProgress();
        }
      } finally {
        setIsDownloading(false);
      }

      return result;
    },
    [token, onProgress, clientId],
  );

  return {
    downloadImages,
    isDownloading,
    progress,
  };
};
